// Copyright (c) 2024-2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { firestore } from '../context/FirebaseConfig';
import { collection, query, where, doc, getDoc, getDocs } from 'firebase/firestore';
import { ensureClaimsInSync } from '../shared_components/utils/FirebaseUtils';

export async function fetchUserInfo(uid, authUser = null) {
  const userInfo = {};

  // Fetch user company & language
  const userDocSnap = await getDoc(doc(firestore, 'users', uid));
  if (!userDocSnap.exists()) {
    return;
  }
  const userData = userDocSnap.data();
  userInfo['info'] = userData;
  const company = userData.company;

  // Sync the auth token's custom claims against the user doc
  if (authUser) {
    await ensureClaimsInSync(authUser, userData);
  }

  // Fetch company display name
  const clientDocSnap = await getDoc(doc(firestore, 'clients', company));
  if (clientDocSnap.exists()) {
    userInfo['company'] = clientDocSnap.data();
  }

  // Fetch devices
  const deviceQuery = query(collection(firestore, 'clients', company, 'devices'), where('client', '==', company));
  const querySnapshot = await getDocs(deviceQuery);
  userInfo['devices'] = {};
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    if (data.enable_stamp === false) return;
    userInfo['devices'][doc.id] = { ...data };
  });

  if ('selected_device_id' in userData && userData['selected_device_id'] in userInfo['devices']) {
    userInfo['device_id'] = userData['selected_device_id'];
  } else {
    userInfo['device_id'] = Object.keys(userInfo['devices'])[0];
  }

  // Fetch stamp permission settings
  try {
    const settingsRef = doc(firestore, 'clients', company, 'client_settings', 'bale_feed_settings');
    const settingsSnap = await getDoc(settingsRef);

    if (settingsSnap.exists()) {
      const settingsData = settingsSnap.data();
      userInfo['stampEnabled'] = settingsData?.stamp_enabled === true;
    } else {
      // If settings don't exist, default to false for security
      userInfo['stampEnabled'] = false;
    }
  } catch (error) {
    console.error('Error fetching stamp permission:', error);
    userInfo['stampEnabled'] = false;
  }

  // Check if user has production lines (devices)
  userInfo['hasProductionLines'] = userInfo['devices'] && Object.keys(userInfo['devices']).length > 0;

  return userInfo;
}

export async function fetchLabelsets(company) {
  const labelsets = {};
  const labelsetsSnapshot = await getDocs(collection(firestore, 'clients', company, 'labelsets'));
  labelsetsSnapshot.forEach((doc) => {
    labelsets[doc.id] = { ...doc.data() };
  });

  return labelsets;
}

export async function fetchSuppliers(company) {
  let suppliers = {};
  const suppliersSnapshot = await getDoc(doc(firestore, 'clients', company, 'client_collections', 'suppliers'));
  if (suppliersSnapshot.exists()) {
    suppliers = suppliersSnapshot.data()?.summary || {};
  }

  return suppliers;
}

export async function fetchProductions(company) {
  let productions = {};
  const productionsSnapshot = await getDoc(
    doc(firestore, 'clients', company, 'client_operations', 'recipe_control', 'production_runs'),
  );
  if (productionsSnapshot.exists()) {
    productions = productionsSnapshot.data()?.summary || {};
  }

  return productions;
}
